import { Button, type ButtonProps } from "./button";
import { Checkbox, type CheckboxProps } from "./checkbox";
import { Icon } from "./icon";
import { Option, Select, type SelectProps } from "./select";
import { cx } from "./style.utils";
import { TableCell } from "./table";
import { Span, type SpanProps } from "./typography";

// -----------------------------------------------------------------------------

export function TableHeadCheckbox({
  checked,
  onCheck,
  disabled,
  ...props
}: Omit<CheckboxProps, "name" | "onCheckedChange"> & {
  onCheck: (checked: boolean) => void;
}) {
  if (disabled) {
    return (
      <div className="size-16px">
        <span className="sr-only">Select</span>
      </div>
    );
  }

  return (
    <Checkbox
      {...props}
      aria-label={checked === true ? "Deselect all rows" : "Select all rows"}
      name="select-all"
      checked={checked}
      onCheckedChange={(value) => onCheck(!!value)}
    />
  );
}

// -----------------------------------------------------------------------------

export function TableCellCheckbox({
  checked,
  onCheck,
  ...props
}: Omit<CheckboxProps, "name" | "onCheckedChange"> & {
  onCheck: (checked: boolean) => void;
}) {
  return (
    <Checkbox
      {...props}
      aria-label={checked ? "Deselect row" : "Select row"}
      name="select-one"
      checked={checked}
      onCheckedChange={(value) => onCheck(!!value)}
    />
  );
}

// -----------------------------------------------------------------------------

/**
 *
 * A button component for table headers that supports sorting indicators.
 * 3 states: ascending, descending, and unsorted.
 *
 * @example
 * header: ({ column }) => (
 *   <div className="text-right">
 *     <TableHeadButton
 *       sortCurr={column.getIsSorted()}
 *       sortNext={column.getNextSortingOrder()}
 *       onClick={() => column.toggleSorting()}
 *       showUnsortedIcon
 *     >
 *       Date
 *     </TableHeadButton>
 *   </div>
 * ),
 */
export function TableHeadButton({
  children,
  className,
  showUnsortedIcon = false,
  sortCurr = false,
  sortNext = false,
  ...props
}: Omit<
  React.ComponentPropsWithRef<"button">,
  "children" | "title" | "type"
> & {
  children: React.ReactNode;
  showUnsortedIcon?: boolean;
  sortCurr?: "asc" | "desc" | false;
  sortNext?: "asc" | "desc" | false;
  onClick: (event: React.MouseEvent<React.ComponentRef<"button">>) => void;
}) {
  return (
    <button
      {...props}
      type="button"
      title={
        sortNext === "asc"
          ? "Sort ascending"
          : sortNext === "desc"
            ? "Sort descending"
            : "Clear sort"
      }
      data-sort-curr={sortCurr}
      data-sort-next={sortNext}
      className={cx(
        "inline-flex items-baseline gap-3px",
        "relative isolate before:absolute before:-inset-10px before:z-[-1]",
        "before:bg-black/5 before:opacity-0 before:transition-opacity",
        "hover:before:opacity-100",
        className,
      )}
    >
      <span>{children}</span>
      <Icon
        name={
          sortCurr === "asc"
            ? "ms-expand-less"
            : sortCurr === "desc"
              ? "ms-expand-more"
              : "ms-unfold-more"
        }
        size="sm"
        className={cx(
          "translate-y-2px",
          sortCurr === false && !showUnsortedIcon ? "invisible" : undefined,
        )}
      />
    </button>
  );
}

// -----------------------------------------------------------------------------

function TableCellFallback() {
  return <span className="text-shade-40">--</span>;
}

export function TableCellContent({
  children,
  fallback,
  showFallback = false,
  ...props
}: React.ComponentPropsWithRef<"div"> & {
  fallback?: React.ReactNode;
  showFallback?: boolean;
}) {
  if (showFallback) {
    return (
      <div {...props} aria-hidden="true">
        {fallback ?? <TableCellFallback />}
      </div>
    );
  }
  return <div {...props}>{children}</div>;
}

/**
 *
 * A table cell component to display when no data is found.
 *
 * @example
 * // standalone
 * <TableCellNotFound colSpan={10}>
 *   No records available.
 * </TableCellNotFound>
 *
 * // within `DataTable`
 * <TableCellNotFound colSpan={columns.length}>
 *   {renderNotFound ? renderNotFound(table) : undefined}
 * </TableCellNotFound>
 */
export function TableCellNotFound({
  children,
  colSpan,
  ...props
}: React.ComponentProps<typeof TableCell> & {
  colSpan: number;
}) {
  return (
    <TableCell {...props} colSpan={colSpan}>
      {children ?? (
        <Span
          palette="neutral-subtle"
          className="grid h-100px place-content-center text-center"
        >
          No data found
        </Span>
      )}
    </TableCell>
  );
}

// -----------------------------------------------------------------------------

interface TablePaginationRowSelectionStatusProps extends Omit<
  SpanProps,
  "children"
> {
  selectableCount?: number;
  selectedCount?: number;
}

export function TablePaginationRowSelectionStatus({
  selectableCount = 0,
  selectedCount = 0,
  className,
  ...props
}: TablePaginationRowSelectionStatusProps) {
  return (
    <Span {...props} size="sm" className={cx("leading-tight", className)}>
      {selectedCount} of {selectableCount} row(s) selected
    </Span>
  );
}

// -----------------------------------------------------------------------------

interface TablePaginationSelectPageSizeProps extends Omit<
  SelectProps,
  "children"
> {
  onChangePageSize: (pageSize: number) => void;
  pageSize?: number;
  pageSizes?: number[] | null;
}

const DEFAULT_PAGE_SIZES = [10, 20, 40];

export function TablePaginationSelectPageSize({
  className,
  onChangePageSize,
  pageSize = 10,
  pageSizes = DEFAULT_PAGE_SIZES,
  ...props
}: TablePaginationSelectPageSizeProps) {
  if (!pageSizes) {
    return null;
  }

  // Include the active page size so the select never shows an unmatched value
  const pageSizesExtended = pageSizes.includes(pageSize)
    ? pageSizes
    : [pageSize, ...pageSizes].sort((a, b) => a - b);

  return (
    <Select
      {...props}
      value={String(pageSize)}
      onValueChange={(nextValue) => onChangePageSize(Number(nextValue))}
      size="sm"
      className={cx("w-fit text-sm", className)}
    >
      {pageSizesExtended.map((size) => (
        <Option key={size} value={String(size)}>
          Show {size}
        </Option>
      ))}
    </Select>
  );
}

// -----------------------------------------------------------------------------

interface TablePaginationPageStatusProps extends Omit<SpanProps, "children"> {
  onChangePageIndex?: (pageIndex: number) => void;
  pageCount: number;
  pageIndex: number;
}

export function TablePaginationPageStatus({
  className,
  onChangePageIndex,
  pageCount,
  pageIndex,
  ...props
}: TablePaginationPageStatusProps) {
  if (!onChangePageIndex) {
    return (
      <Span
        {...props}
        size="sm"
        className={cx("whitespace-nowrap leading-tight", className)}
      >
        Page <strong>{pageIndex + 1}</strong> of <strong>{pageCount}</strong>
      </Span>
    );
  }

  return (
    <Span
      {...props}
      size="sm"
      className={cx("flex items-center gap-5px leading-tight", className)}
    >
      <span className="whitespace-nowrap">Go to Page</span>{" "}
      <Select
        value={String(pageIndex)}
        onValueChange={(pageNumber) => onChangePageIndex(Number(pageNumber))}
        size="sm"
        className="mx-5px w-fit min-w-fit"
      >
        {[...Array(pageCount).keys()].map((pageNumber) => (
          <Option key={pageNumber} value={String(pageNumber)}>
            {pageNumber + 1}
          </Option>
        ))}
      </Select>{" "}
      <span className="whitespace-nowrap">
        of <strong>{pageCount}</strong>
      </span>
    </Span>
  );
}

// -----------------------------------------------------------------------------

interface TablePaginationButtonProps extends Omit<
  ButtonProps,
  "children" | "variant"
> {}

// ms-keyboard-double-arrow-left-right

export function TablePaginationButtonFirst({
  ...props
}: TablePaginationButtonProps) {
  return (
    <Button {...props} size="sm" variant="outline" iconOnly>
      <Icon name="ms-keyboard-double-arrow-left" />
      <span className="sr-only">First Page</span>
    </Button>
  );
}

export function TablePaginationButtonPrevious({
  ...props
}: TablePaginationButtonProps) {
  return (
    <Button {...props} size="sm" variant="outline" iconOnly>
      <Icon name="ms-navigate-prev" />
      <span className="sr-only">Previous Page</span>
    </Button>
  );
}

export function TablePaginationButtonNext({
  ...props
}: TablePaginationButtonProps) {
  return (
    <Button {...props} size="sm" variant="outline" iconOnly>
      <span className="sr-only">Next Page</span>
      <Icon name="ms-navigate-next" />
    </Button>
  );
}

export function TablePaginationButtonLast({
  ...props
}: TablePaginationButtonProps) {
  return (
    <Button {...props} size="sm" variant="outline" iconOnly>
      <span className="sr-only">Last Page</span>
      <Icon name="ms-keyboard-double-arrow-right" />
    </Button>
  );
}
